import { Status, NonClientError } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import { useState, useCallback } from 'react';
import useQueryParameters from 'hooks/useQueryParameters';
import catchOnly from 'utils/catchOnly';

const PATH = '/api/financing_apple_wallet_provision';

type FinancingAppleWalletProvisionHook = () => [
  (
    params: FinancingAppleWalletProvisionRequest,
  ) => Promise<FinancingAppleWalletProvisionResult>,
  Status,
  boolean,
];

export interface FinancingAppleWalletProvisionRequest {
  issuanceId: string;
}

export interface FinancingAppleWalletProvisionResult {
  walletId: string;
  state?: string;
  jws?: {};
}

export class FinancingAppleWalletProvisionError extends Error {}

const useFinancingAppleWalletProvision: FinancingAppleWalletProvisionHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const { mockFAWP } = useQueryParameters();

  const financingAppleWalletProvisionRequest = useCallback(
    async (
      params: FinancingAppleWalletProvisionRequest,
    ): Promise<FinancingAppleWalletProvisionResult> => {
      setError(false);

      try {
        const responsePromise = fetch(PATH, {
          method: 'POST',
          body: {
            ...params,
            mockResponse: mockFAWP,
          },
        });
        const response = await responsePromise;

        if (response.ok) {
          const result: FinancingAppleWalletProvisionResult = await response.json();
          return Promise.resolve(result);
        } else {
          setError(true);
          return Promise.reject(new FinancingAppleWalletProvisionError());
        }
      } catch (e) {
        if (catchOnly(e, NonClientError)) {
          setError(e.message);
          return Promise.reject(e);
        }
        // Re-throw so that any exceptions can be handled downstream.
        throw e;
      }
    },
    [fetch, mockFAWP],
  );

  return [financingAppleWalletProvisionRequest, status, error];
};

export default useFinancingAppleWalletProvision;
